import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { SparePart } from "@/mocks/products";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { imageFor } from "@/lib/productImages";
import { enquireHref } from "@/lib/enquire";

interface Props {
  title: string;
  subtitle: string;
  parts: SparePart[];
  /** Optional panel rendered at the very top of the listing, before filters. */
  notice?: ReactNode;
}

export default function ProductGrid({ title, subtitle, parts, notice }: Props) {
  const { t } = useTranslation();
  const { pricing } = useSiteSettings();
  const showPrices = pricing.show_prices;
  const [query, setQuery] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [open, setOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const makes = useMemo(() => {
    const set = new Set<string>();
    parts.forEach((p) => set.add(p.make));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [parts]);

  const models = useMemo(() => {
    const set = new Set<string>();
    parts.forEach((p) => {
      if (!make || p.make === make) set.add(p.model);
    });
    return Array.from(set)
      .filter((m) => m && m !== "All makes & models")
      .sort((a, b) => a.localeCompare(b));
  }, [parts, make]);

  const indexById = useMemo(() => {
    const map = new Map<string, number>();
    parts.forEach((p, i) => map.set(p.id, i));
    return map;
  }, [parts]);

  // Live type-ahead suggestions: as the user types, show matching parts.
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return parts
      .filter((p) =>
        [p.name, p.make, p.model, p.fitment, p.yearRange].some((f) =>
          f.toLowerCase().includes(q)
        )
      )
      .slice(0, 7);
  }, [parts, query]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return parts.filter((p) => {
      const matchMake = !make || p.make === make;
      const matchModel = !model || p.model === model;
      const matchQuery =
        !q ||
        [p.name, p.make, p.model, p.fitment, p.yearRange].some((f) =>
          f.toLowerCase().includes(q)
        );
      return matchMake && matchModel && matchQuery;
    });
  }, [parts, query, make, model]);

  // Close the suggestion dropdown when clicking outside the search box.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset the model selection when the make changes.
  useEffect(() => {
    setModel("");
  }, [make]);

  const clearFilters = () => {
    setQuery("");
    setMake("");
    setModel("");
  };

  return (
    <div className="w-full bg-neutral-50">
      {/* Header / hero band */}
      <section className="w-full bg-brand-dark py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6 text-center">
          <span className="text-xs font-medium uppercase tracking-widest text-brand-gold">
            {t("grid.ourStock")}
          </span>
          <h1 className="mt-2 font-heading text-3xl md:text-4xl font-semibold uppercase text-white">
            {title}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-white/60 leading-relaxed">
            {subtitle}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
        {notice && <div className="mb-8">{notice}</div>}
        <div className="flex items-center justify-between pb-6">
          <p className="text-sm text-neutral-600">
            <span className="font-semibold text-brand-dark">{filtered.length}</span>{" "}
            {t("grid.itemsInStock", { count: filtered.length })}
          </p>
          <span className="hidden sm:inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
            <i className="ri-truck-line"></i>
            {t("grid.shipping")}
          </span>
        </div>

        {/* Search + make/model filters */}
        <div className="mb-8 grid grid-cols-1 gap-3 md:grid-cols-[1fr_190px_190px]">
          {/* Search with live type-ahead */}
          <div ref={searchRef} className="relative">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder={t("grid.searchPlaceholder")}
              className="w-full rounded-md border border-neutral-200 bg-white py-2.5 pl-9 pr-3 text-sm text-neutral-700 placeholder:text-neutral-400 focus:border-brand-gold focus:outline-none"
            />
            {open && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-md border border-neutral-200 bg-white">
                {suggestions.map((s) => (
                  <Link
                    key={s.id}
                    to={`/product/${s.id}`}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 border-b border-neutral-100 px-3 py-2.5 transition-colors last:border-b-0 hover:bg-neutral-50"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-neutral-100 text-neutral-500">
                      <i className="ri-car-line text-base"></i>
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium text-brand-dark">
                        {s.name}
                      </span>
                      <span className="truncate text-xs text-neutral-500">
                        {s.make}
                        {s.model ? ` · ${s.model}` : ""}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Make filter */}
          <div className="relative">
            <i className="ri-filter-3-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400"></i>
            <select
              value={make}
              onChange={(e) => setMake(e.target.value)}
              className="w-full appearance-none rounded-md border border-neutral-200 bg-white py-2.5 pl-9 pr-8 text-sm text-neutral-700 focus:border-brand-gold focus:outline-none"
            >
              <option value="">{t("grid.allMakes")}</option>
              {makes.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400"></i>
          </div>

          {/* Model filter */}
          <div className="relative">
            <i className="ri-car-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400"></i>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={models.length === 0}
              className="w-full appearance-none rounded-md border border-neutral-200 bg-white py-2.5 pl-9 pr-8 text-sm text-neutral-700 focus:border-brand-gold focus:outline-none disabled:bg-neutral-100 disabled:text-neutral-400"
            >
              <option value="">{t("grid.allModels")}</option>
              {models.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400"></i>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-neutral-300 bg-white py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 text-sm text-neutral-600">{t("grid.noResults")}</p>
            <button
              type="button"
              onClick={clearFilters}
              className="mt-4 rounded-md bg-brand-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white whitespace-nowrap"
            >
              {t("grid.clearFilters")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
            {filtered.map((part) => {
              const idx = indexById.get(part.id) ?? 0;
              return (
                <article
                  key={part.id}
                  className="group flex flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white transition hover:border-brand-gold"
                >
                  <Link
                    to={`/product/${part.id}`}
                    className="relative block h-44 w-full overflow-hidden bg-neutral-100"
                  >
                    <img
                      src={imageFor(part, idx)}
                      alt={`${part.name} - used auto parts Australia`}
                      title={`${part.name} - used auto parts Australia`}
                      className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                    <span className="absolute right-3 top-3 rounded-full bg-brand-dark/80 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur">
                      {part.stock}
                    </span>
                  </Link>

                  <div className="flex flex-1 flex-col p-5">
                    <span className="text-xs font-medium uppercase tracking-wide text-brand-gold">
                      {part.category} · {part.condition}
                    </span>
                    <h3 className="mt-2 font-heading text-base md:text-lg font-semibold text-brand-dark">
                      <Link to={`/product/${part.id}`} className="transition hover:text-brand-gold">
                        {part.name}
                      </Link>
                    </h3>
                    <div className="mt-3 space-y-1.5 text-xs text-neutral-500">
                      <p className="flex items-center gap-2">
                        <i className="ri-calendar-line"></i>
                        {part.yearRange}
                      </p>
                      <p className="flex items-center gap-2">
                        <i className="ri-tools-line"></i>
                        {part.fitment}
                      </p>
                    </div>

                    <div className="mt-auto flex flex-col gap-3 border-t border-neutral-100 pt-4">
                      <div className="flex items-center justify-between">
                        {showPrices && (
                          <div>
                            <p className="text-xs text-neutral-400">{t("grid.price")}</p>
                            <p className="font-heading text-xl font-semibold text-brand-dark">
                              {part.price}
                            </p>
                          </div>
                        )}
                        <p className="ml-auto text-right text-[11px] leading-tight text-neutral-400">
                          {part.warranty}
                        </p>
                      </div>
                      <a
                        href={enquireHref(part, showPrices)}
                        className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-gold px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white whitespace-nowrap"
                      >
                        <i className="ri-message-3-line"></i>
                        {t("grid.enquire")}
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}